import axios from 'axios';
import { API_BASE_URL } from './Base';

/**
 * 401 응답에 대한 자동 refresh + 재시도 인터셉터.
 *
 * 동작:
 *  - 401이 아니면 그대로 reject.
 *  - 401이지만 이미 _retry 처리됐거나 refresh endpoint 자체 호출이면 더 이상 재시도하지 않음 (무한 루프 방지).
 *  - 그 외 401: 인-flight refresh 1회로 합쳐 호출 → 성공 시 원 요청 재시도, 실패 시 원 401 reject.
 *
 * 모듈 스코프 inFlightRefresh로 동시에 들어온 401들을 단일 refresh 호출로 dedupe.
 */
const REFRESH_PATH = 'admin/token';
let inFlightRefresh = null;

// refresh endpoint 자체 호출인지 판정 — substring 'token' 포함이 아닌 엄밀 매칭.
// 다른 API 경로에 '/token'이 들어가도(예: admin/device/token-rotate) 재시도가 차단되지 않도록 함.
function isRefreshRequest(url) {
    if (!url) return false;
    return url === REFRESH_PATH || url.endsWith(`/${REFRESH_PATH}`);
}

function refreshSession() {
    if (inFlightRefresh) return inFlightRefresh;
    const refresher = axios.create({
        baseURL: API_BASE_URL,
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
    });
    inFlightRefresh = refresher.post(REFRESH_PATH).finally(() => {
        inFlightRefresh = null;
    });
    return inFlightRefresh;
}

export function attachAuthInterceptor(instance) {
    instance.interceptors.response.use(
        (response) => response,
        async (error) => {
            const status = error?.response?.status;
            const original = error?.config;
            if (status !== 401 || !original) return Promise.reject(error);
            if (isRefreshRequest(original.url)) return Promise.reject(error);
            if (original._retry) return Promise.reject(error);
            original._retry = true;
            try {
                await refreshSession();
            } catch (refreshErr) {
                return Promise.reject(error);
            }
            return instance.request(original);
        },
    );
}
